import { useEffect, useState } from "react";

export interface NetworkStats {
  humansActive: number;
  labelsVerifiedTotal: number;
  datasetsShipped: number;
  directPayPaidTotalCents: number;
  asOf: string;
}

export const useNetworkStats = (): NetworkStats | null => {
  const [stats, setStats] = useState<NetworkStats | null>(null);
  useEffect(() => {
    let cancelled = false;
    fetch("/api/public/network-stats")
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        if (!cancelled && data) setStats(data);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);
  return stats;
};

/**
 * Em-dash placeholder for stats that aren't loaded yet OR that read as
 * literal zero. Treating 0 as "—" matches the marketing-surface honesty
 * rule (final M1.6 code review, Important #2): at launch, 3 of the 4
 * stats are backed by entities that don't ship until later milestones
 * (Annotation, Dataset, LedgerEntry), so the backend returns 0 by
 * contract. Rendering "0 / 0 / $0" reads as "dead network"; rendering
 * "— / — / —" reads honestly as "this number is on the way." When a
 * field's backing entity ships and returns positive values, the stat
 * switches to its real number automatically.
 */
const PLACEHOLDER = "\u2014"; // em-dash

export const formatNumber = (n: number | null | undefined): string =>
  n != null && n > 0 ? n.toLocaleString("en-US") : PLACEHOLDER;

export const formatCents = (cents: number | null | undefined): string => {
  if (cents == null || cents <= 0) return PLACEHOLDER;
  const dollars = cents / 100;
  if (dollars >= 1_000_000) return `$${(dollars / 1_000_000).toFixed(1)}M`;
  if (dollars >= 1_000) return `$${(dollars / 1_000).toFixed(0)}K`;
  return `$${dollars.toFixed(0)}`;
};
